import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import DataGrid, {
  Column, FilterRow, HeaderFilter, SearchPanel, Export, Paging, Pager,
  Grouping, GroupPanel, Summary, TotalItem, GroupItem, Toolbar, Item as ToolbarItem
} from "devextreme-react/data-grid";
import { Button } from 'devextreme-react/button';
import DateBox from "devextreme-react/date-box";
import SelectBox from "devextreme-react/select-box";
import TextBox from "devextreme-react/text-box";
import notify from 'devextreme/ui/notify';

import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from "exceljs";
import { saveAs } from 'file-saver';
import API_URL from '../config';

const SOFT_BIN_API = `${API_URL}/api/oracle/soft-bin-report`;
const CUST_API_URL = `${API_URL}/api/oracle/cust`;
const DEVICE_API_URL = `${API_URL}/api/oracle/device`;
const operList = ['PTST',  'PTST2', 'PTST3', 'PTST4'];

const styles = `
.loading-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255, 255, 255, 0.7); display: flex; flex-direction: column; justify-content: center; align-items: center; z-index: 10; }
.loading-text { margin-top: 10px; font-size: 16px; color: #333; }
@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.loading-spinner { border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%; width: 40px; height: 40px; animation: spin 1s linear infinite; }
`;

export default function DeviMapBinReport({ user }) {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comboLoading, setComboLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(firstDayOfMonth);
  const [dateTo, setDateTo] = useState(today);
  const [selectedCust, setSelectedCust] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [selectedOper, setSelectedOper] = useState(operList[0]);
  const [lotNo, setLotNo] = useState('');
  const [custList, setCustList] = useState([]);
  const [deviceList, setDeviceList] = useState([]);
  const [binColumns, setBinColumns] = useState([]);
  const [error, setError] = useState("");

  const formatDate = (date) => date ? `${date.getFullYear()}${(date.getMonth() + 1).toString().padStart(2, "0")}${date.getDate().toString().padStart(2, "0")}` : '';

  useEffect(() => {
    setComboLoading(true);
    axios.get(CUST_API_URL)
      .then(res => {
        if (res.data.retCode === 0) setCustList(res.data.data || []);
        else throw new Error(res.data.retMsg);
      })
      .catch(err => notify(`고객사 목록 로딩 실패: ${err.message}`, 'error', 3000))
      .finally(() => setComboLoading(false));
  }, []);

  useEffect(() => {
    if (user && user.role === "CUST" && custList.length > 0) {
      const userCust = custList.find(c => c.CODE_CD.trim().toUpperCase() === user.username.trim().toUpperCase());
      if (userCust) setSelectedCust(userCust.CODE_CD);
    }
  }, [user, custList]);

  useEffect(() => {
    if (!selectedCust) {
      setDeviceList([]);
      setSelectedDevice(null);
      return;
    }
    setDeviceLoading(true);
    axios.get(DEVICE_API_URL, { params: { custCd: selectedCust } })
      .then(res => {
        if (res.data.retCode === 0) {
          const devices = res.data.data || [];
          setDeviceList(devices.filter(d => d.CODE_CD !== 'ALL'));
        } else {
          throw new Error(res.data.retMsg);
        }
        setSelectedDevice(null);
      })
      .catch(err => notify(`디바이스 목록 로딩 실패: ${err.message}`, 'error', 3000))
      .finally(() => setDeviceLoading(false));
  }, [selectedCust]);
  
  const processDataAndColumns = (rawData) => {
    const summaryData = rawData.filter(item => item.DATA_TYPE === 'SUMMARY');
    const detailsData = rawData.filter(item => item.DATA_TYPE === 'DETAILS');
    if (summaryData.length === 0) return { finalData: [], dynamicColumns: [] };

    const binStructureMap = new Map();
    detailsData.forEach(bin => {
      if (bin.CHAR_BIN_SEQ != null && !binStructureMap.has(bin.CHAR_BIN_SEQ)) {
        binStructureMap.set(bin.CHAR_BIN_SEQ, {
          hbin: bin.CHAR_BIN || `BIN ${bin.CHAR_BIN_SEQ}`,
          sbin: bin.CHAR_BIN_SEQ,
          desc: bin.BIN_DESC || `Bin No. ${bin.CHAR_BIN_SEQ}`
        });
      }
    });
    
        // ★★★ 핵심 수정: .sort() 호출을 제거하여 DB 조회 순서를 유지합니다. ★★★
        const sortedBinStructures = Array.from(binStructureMap.values());

    const binDetailsLookup = detailsData.reduce((acc, bin) => {
        const key = `${bin.LOT_NO}|${bin.WAFR_NO}`;
        if (!acc[key]) acc[key] = {};
        acc[key][bin.CHAR_BIN_SEQ] = bin.LOT_QTY;
        return acc;
    }, {});
    const finalData = summaryData.map((summaryRow, index) => {
      const newRow = { ...summaryRow, gridId: `${summaryRow.LOT_NO}_${summaryRow.WAFR_NO}_${index}` };
      const key = `${summaryRow.LOT_NO}|${summaryRow.WAFR_NO}`;
      const relevantBins = binDetailsLookup[key] || {};
      sortedBinStructures.forEach(binInfo => {
        newRow[`BIN_Q_${binInfo.sbin}`] = relevantBins[binInfo.sbin] || 0;
      });
      return newRow;
    });
  

 
    // hbinGroups의 키 정렬 로직도 제거하거나, 순서를 보장하는 Map으로 변경
    const hbinGroupsMap = new Map();
    sortedBinStructures.forEach(binInfo => {
        const hbinKey = binInfo.hbin;
        if (!hbinGroupsMap.has(hbinKey)) {
            hbinGroupsMap.set(hbinKey, []);
        }
        hbinGroupsMap.get(hbinKey).push(binInfo);
    });


    const dynamicColumns = Array.from(hbinGroupsMap.keys()).map(hbin => ({
        caption: String(hbin), isBand: true, alignment: 'center',
        columns: hbinGroupsMap.get(hbin).map(binInfo => ({
            caption: String(binInfo.sbin), isBand: true, alignment: 'center',
            columns: [{
                caption: binInfo.desc,
                dataField: `BIN_Q_${binInfo.sbin}`,
                dataType: "number", alignment: "right", width: 120,
            }]
        }))
    }));

    return { finalData, dynamicColumns };
};
  
  const fetchData = useCallback(async () => {
    if (!selectedCust || !selectedDevice) {
      notify("Customer와 Device는 필수 선택 항목입니다.", "warning", 2000);
      return;
    }
    setLoading(true);
    setError("");
    setData([]);
    setBinColumns([]);
    try {
      const params = {
        custCd: selectedCust, deviCd: selectedDevice,
        operCd: selectedOper, 
        lotNo: lotNo.trim() || 'ALL',
        dateFrom: formatDate(dateFrom), dateTo: formatDate(dateTo),
      };
      const response = await axios.get(SOFT_BIN_API, { params });
      if (response.data.retCode !== 0) throw new Error(response.data.retMsg);
      const rawData = response.data.data;
      if (rawData.length === 0) {
        notify("조회된 데이터가 없습니다.", "info", 2000);
        return;
      }
      const { finalData, dynamicColumns } = processDataAndColumns(rawData);
      setData(finalData);
      setBinColumns(dynamicColumns);
    } catch (error) {
      setError(`데이터 조회 실패: ${error.message}`);
      notify(`데이터 조회 실패: ${error.message}`, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, [selectedCust, selectedDevice, selectedOper, lotNo, dateFrom, dateTo]);
  
  const fixedColumns = useMemo(() => [
     { dataField: "CUST_CD", caption: "CUSTOMER", width: 120 }, 
     { dataField: "DEVI_CD", caption: "MAT_ID", width: 150 }, 
     { dataField: "OPER_CD", caption: "OPER", width: 100 }, 
     { dataField: "LOT_NO", caption: "LOT_ID", width: 120 }, 
     { dataField: "WAFR_NO", caption: "WAFR_NO", width: 80 }, 
     { 
      dataField: "STRT_TM", 
      caption: "START_TIME", 
      width: 170,
      dataType: "datetime", // 'date'에서 'datetime'으로 변경
      format: "yyyy-MM-dd HH:mm:ss" 
    }, 
    { 
      dataField: "ENDD_TM", 
      caption: "END_TIME", 
      width: 170,
      dataType: "datetime", // 'date'에서 'datetime'으로 변경
      format: "yyyy-MM-dd HH:mm:ss"
    },
     { dataField: "IN_QTY", caption: "IN_QTY", dataType: "number", alignment: "right" },
     { dataField: "OUT_QTY", caption: "OUT_QTY", dataType: "number", alignment: "right" },     
    ], []);
  
  const onExporting = useCallback((e) => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Soft_Bin_Report");
    exportDataGrid({ component: e.component, worksheet, autoFilterEnabled: true })
      .then(() => workbook.xlsx.writeBuffer())
      .then(buffer => saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Soft_Bin_Report.xlsx"));
    e.cancel = true;
  }, []);
  
  const renderColumns = (cols) => {
    return cols.map((col, index) => {
      const key = col.dataField || col.caption + index;
      if (col.isBand) {
        return <Column key={key} caption={col.caption} alignment="center">{col.columns && renderColumns(col.columns)}</Column>;
      }
      return <Column key={key} {...col} />;
    });
  };

  return (
    <div style={{ padding: 20, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <style>{styles}</style>
      <h2 style={{ marginBottom: '20px' }}>Soft Bin Detail Report</h2>
      <div style={{ display: "flex", gap: 15, flexWrap: "wrap", alignItems: 'center', marginBottom: '20px' }}>
        <DateBox value={dateFrom} onValueChanged={e => setDateFrom(e.value)} label="From" width={180} type="date" displayFormat="yyyy-MM-dd"/>
        <DateBox value={dateTo} onValueChanged={e => setDateTo(e.value)} label="To" width={180} type="date" displayFormat="yyyy-MM-dd"/>
        <SelectBox
          dataSource={custList} value={selectedCust} valueExpr="CODE_CD" displayExpr="CODE_CD"
          onValueChanged={e => setSelectedCust(e.value)} searchEnabled label="Customer" width={200}
          disabled={comboLoading || user?.role === "CUST"} showClearButton={user?.role !== "CUST"}
          placeholder="Select Customer"
        />
        <SelectBox
          dataSource={deviceList} value={selectedDevice} valueExpr="CODE_CD" displayExpr="CODE_CD"
          onValueChanged={e => setSelectedDevice(e.value)} searchEnabled label="Device" width={220}
          placeholder="Select Device" disabled={deviceLoading || !selectedCust} showClearButton
        />
            <SelectBox 
          dataSource={operList} 
          value={selectedOper} 
          onValueChanged={e => setSelectedOper(e.value)} 
          label="Operation" 
          width={150} 
        />
        <TextBox value={lotNo} onValueChanged={e => setLotNo(e.value)} placeholder="Enter Lot No." label="Lot No" width={180} />
        <Button text="Search" type="default" icon="search" onClick={fetchData} disabled={loading} />
      </div>
      {error && <p style={{ color: "red", fontWeight: "bold" }}>{error}</p>}

      <div style={{ 
          flex: 1, 
          position: 'relative',
          maxWidth: '100%' // 1. 최대 너비를 90%로 제한
         
       }}>
        {loading && <div className="loading-overlay"><div className="loading-spinner"></div><div className="loading-text">Loading...</div></div>}
        <DataGrid dataSource={data} keyExpr="gridId" showBorders={true} columnAutoWidth={true} height="100%" onExporting={onExporting} allowColumnResizing={true} hoverStateEnabled={true}>
          <Paging defaultPageSize={20} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[20, 50, 100]} showInfo={true} />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <Export enabled={true} />
          <GroupPanel visible={true} />
          <Grouping autoExpandAll={false} />
          <SearchPanel visible={true} width={240} placeholder="전체 검색..." />
          <Toolbar>
            <ToolbarItem name="groupPanel" />
            <ToolbarItem location="after" name="exportButton" />
            <ToolbarItem location="after" name="searchPanel" />
          </Toolbar>
          
          <Column caption="Lot Summary" fixed={true} isBand={true}>
            {fixedColumns.map(col => <Column key={col.dataField} {...col} />)}
          </Column>
          <Column caption="Bin Details" isBand={true}>
            {renderColumns(binColumns)}
          </Column>
          <Summary>
            <TotalItem column="LOT_NO" summaryType="count" displayFormat="Total Rows: {0}" />
            <TotalItem column="IN_QTY" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" />            
            <GroupItem column="LOT_NO" summaryType="count" displayFormat="{0} items" />
            <GroupItem column="IN_QTY" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" showInGroupFooter={true} />
            <GroupItem column="GOOD_QTY" summaryType="sum" valueFormat="thousands" displayFormat="Sum: {0}" showInGroupFooter={true} />
          </Summary>
        </DataGrid>
      </div>
    </div>
  );
}